// §4.10 What-if sandbox, the list (`/planning/scenarios`, P3-J J-4, D-471,
// D-474): the facility's scenarios, newest first (row 81), with the status
// chip, the base version, who created each and when, and the late-orders
// delta. Read and written on `planning.scenario.manage`. A row opens its
// result; New scenario opens the builder. A row still solving re-asks every
// two seconds. On a phone the table is cards and the list reads only.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { FilterBar, type FilterChip } from "../../../../components/filter-bar";
import { Select } from "../../../../components/select";
import { EmptyState, ErrorState } from "../../../../components/states";
import { useDesktop } from "../../../../components/use-desktop";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { PlanningHeader } from "../components/planning-header";
import { ScenarioStatusChip } from "../components/scenario-bits";
import { useMemberNames } from "../hooks/mps-queries";
import { usePlanningPermissions } from "../hooks/permissions";
import {
  SCENARIO_STATUSES,
  useScenarios,
  type ScenarioHeader,
  type ScenarioStatus,
} from "../hooks/scenario-queries";
import { shortId } from "../lib/mrp-lib";
import { useOffline } from "../lib/planning-lib";
import { signedText, validateScenariosSearch } from "../lib/scenario-lib";
import { zonedStamp } from "../lib/schedule-lib";
import { usePlanningFacility } from "../planning-layout";

export function PlanningScenariosScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const desktop = useDesktop();
  const { facility } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const offline = useOffline();
  const lang = i18n.language;
  const members = useMemberNames(perms.canReadMembers);

  const rawSearch = useSearch({ strict: false });
  const search = useMemo(
    () => validateScenariosSearch(rawSearch as Record<string, unknown>),
    [rawSearch],
  );
  const setStatus = (status: ScenarioStatus | undefined) => {
    void navigate({
      to: "/planning/scenarios",
      search: status !== undefined ? { status } : {},
      replace: true,
    });
  };

  // Cursor paging; a filter or facility change starts again from page one.
  const pageKey = `${facility.id}|${search.status ?? ""}`;
  const [paging, setPaging] = useState<{ key: string; cursors: (string | undefined)[] }>({
    key: pageKey,
    cursors: [undefined],
  });
  if (paging.key !== pageKey) setPaging({ key: pageKey, cursors: [undefined] });
  const cursors = paging.key === pageKey ? paging.cursors : [undefined];
  const page = cursors.length - 1;
  const list = useScenarios(facility.id, search.status, cursors[page]);
  const rows = useMemo(() => list.data?.items ?? [], [list.data]);

  const authError = list.error instanceof UnauthorizedError ? list.error : undefined;
  useEffect(() => {
    if (authError !== undefined) void navigate({ to: "/login" });
  }, [authError, navigate]);

  const who = (row: ScenarioHeader) =>
    row.created_by_agent
      ? t("planning.scenarios.agent")
      : (members.byId.get(row.created_by) ?? shortId(row.created_by));

  const columns: DataTableColumn<ScenarioHeader>[] = [
    {
      key: "name",
      header: t("planning.scenarios.columns.name"),
      phonePriority: 1,
      render: (row) => <span data-testid="planning-scenario-name">{row.name}</span>,
    },
    {
      key: "status",
      header: t("planning.scenarios.columns.status"),
      kind: "status",
      phonePriority: 2,
      render: (row) => <ScenarioStatusChip status={row.status} />,
    },
    {
      key: "base",
      header: t("planning.scenarios.columns.base"),
      render: (row) => (
        <span
          className={row.base_superseded ? "planning-warn" : undefined}
          data-testid="planning-scenario-base"
          data-superseded={String(row.base_superseded)}
        >
          {t("planning.scenarios.baseVersion", { version: row.base_version })}
        </span>
      ),
    },
    {
      key: "created",
      header: t("planning.scenarios.columns.created"),
      phonePriority: 4,
      render: (row) =>
        t("planning.scenarios.created", {
          who: who(row),
          time: zonedStamp(row.created_at, lang, facility.timezone),
        }),
    },
    {
      key: "lateDelta",
      header: t("planning.scenarios.columns.lateDelta"),
      kind: "qty",
      phonePriority: 3,
      render: (row) =>
        row.late_orders_delta === null ? (
          <span className="planning-note">{t("planning.scenarios.notSolved")}</span>
        ) : (
          <span
            className={row.late_orders_delta > 0 ? "planning-bad" : undefined}
            data-testid="planning-scenario-late-delta"
            data-delta={String(row.late_orders_delta)}
          >
            {signedText(row.late_orders_delta, lang)}
          </span>
        ),
    },
  ];

  const header = (
    <PlanningHeader
      title={t("planning.scenarios.title")}
      actions={
        desktop ? (
          <Button
            variant="primary"
            disabled={offline}
            data-testid="planning-scenarios-new"
            onClick={() => {
              void navigate({ to: "/planning/scenarios/new" });
            }}
          >
            {t("planning.scenarios.newScenario")}
          </Button>
        ) : undefined
      }
    />
  );

  if (list.error !== null && authError === undefined && list.data === undefined) {
    return (
      <section className="planning-screen" data-testid="planning-scenarios">
        {header}
        <ErrorState
          title={t("planning.scenarios.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void list.refetch()}
          requestId={requestIdOf(list.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      </section>
    );
  }

  const chips: FilterChip[] =
    search.status !== undefined
      ? [
          {
            key: "status",
            label: t("planning.scenarios.filterTag.status", {
              status: t(`planning.scenarios.status.${search.status}`),
            }),
            onRemove: () => {
              setStatus(undefined);
            },
          },
        ]
      : [];

  const emptyState =
    search.status !== undefined ? (
      <EmptyState
        title={t("planning.scenarios.filteredEmptyTitle")}
        body={t("planning.scenarios.filteredEmptyBody", {
          status: t(`planning.scenarios.status.${search.status}`),
        })}
        ctaLabel={t("planning.common.clearFilters")}
        onCta={() => {
          setStatus(undefined);
        }}
      />
    ) : (
      <EmptyState
        title={t("planning.scenarios.emptyTitle")}
        body={t("planning.scenarios.emptyBody")}
        {...(desktop && !offline
          ? {
              ctaLabel: t("planning.scenarios.newScenario"),
              onCta: () => {
                void navigate({ to: "/planning/scenarios/new" });
              },
            }
          : {})}
      />
    );

  return (
    <section className="planning-screen" data-testid="planning-scenarios">
      {header}
      <FilterBar
        data-testid="planning-scenarios-filters"
        filters={{
          inline: (
            <Field label={t("planning.scenarios.filters.status")}>
              <Select
                value={search.status ?? ""}
                data-testid="planning-scenarios-filter-status"
                onChange={(e) => {
                  setStatus((e.target.value || undefined) as ScenarioStatus | undefined);
                }}
              >
                <option value="">{t("planning.scenarios.filters.any")}</option>
                {SCENARIO_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`planning.scenarios.status.${s}`)}
                  </option>
                ))}
              </Select>
            </Field>
          ),
          label: t("planning.common.filtersCount", { count: chips.length }),
          title: t("planning.common.filters"),
          doneLabel: t("planning.common.done"),
        }}
        chips={chips}
        removeLabel={(chip) => t("planning.common.removeFilter", { filter: chip.label })}
      />
      <Card>
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          rowTestId="planning-scenario-row"
          rowProps={(r) => ({
            "data-scenario-id": r.id,
            "data-status": r.status,
          })}
          onRowClick={(r) => {
            void navigate({ to: "/planning/scenarios/$scenarioId", params: { scenarioId: r.id } });
          }}
          loading={list.isPending}
          loadingLabel={t("planning.scenarios.loading")}
          caption={t("planning.scenarios.title")}
          fit
          empty={emptyState}
          error={
            list.error !== null && authError === undefined ? (
              <ErrorState
                title={t("planning.scenarios.loadError")}
                retryLabel={t("planning.common.retry")}
                onRetry={() => void list.refetch()}
                requestId={requestIdOf(list.error)}
                requestIdLabel={t("planning.common.requestId")}
              />
            ) : undefined
          }
          pager={{
            range: t("planning.common.page", { page: page + 1 }),
            page: page + 1,
            onPage: (n) => {
              if (n > page + 1 && list.data?.next_cursor) {
                const next = list.data.next_cursor;
                setPaging({ key: pageKey, cursors: [...cursors, next] });
              } else if (n < page + 1) setPaging({ key: pageKey, cursors: cursors.slice(0, -1) });
            },
            prevLabel: t("planning.common.previous"),
            nextLabel: t("planning.common.next"),
            hasPrev: page > 0,
            hasNext: (list.data?.next_cursor ?? null) !== null,
          }}
        />
      </Card>
    </section>
  );
}
